import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  ApiV2InfiniteQueryOptions,
  apiV2Request,
  getDefaultInfiniteQueryOptionsV2,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";
import {
  InvoiceListRequestType,
  InvoiceListResponseType,
  InvoiceListWithCountResponseType,
} from "@sellar-io/api-v2/types";

export const useInvoiceList = <TData = InvoiceListResponseType,>(
  params: InvoiceListRequestType,
  options?: ApiV2InfiniteQueryOptions<TData>,
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useInfiniteQuery({
    queryKey: [user?.uid, business?.id, ServerStateKeys.Invoice, params.search],
    queryFn: ({ pageParam }) =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: "invoice",
        params: {
          ...params,
          offset: pageParam,
        },
      }).then((data) => apiV2AssertResponse<TData>(data).data),
    ...getDefaultInfiniteQueryOptionsV2({
      context: { scope: "business", user, business: business },
      options: { ...options, limit: params.limit },
    }),
  });
};

export const useInvoicePaginatedList = <
  TData = InvoiceListWithCountResponseType,
>(
  params: InvoiceListRequestType,
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useQuery<TData>({
    queryKey: [
      user?.uid,
      business?.id,
      ServerStateKeys.OpeningOrderFee,
      params.limit,
      params.offset,
      params.search,
      params.status,
      params.type,
      params.sortBy,
      params.sortDir,
    ],
    queryFn: () =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: "invoice/with-count",
        params,
      }).then((data) => apiV2AssertResponse<TData>(data).data),
    placeholderData: (prev) => prev,
  });
};
